import { useEffect } from "react";

import { useAuth } from "@/hooks/useAuth";
import { useOrganizationContext } from "@/lib/context/OrganizationContext";

import { identifyUser, setOrganisation } from "./client";

/**
 * Keeps PostHog's idea of who and where in step with auth state. Renders nothing.
 *
 * `distinct_id` is the Supabase auth user UUID, so a person is the same person across
 * browsers; the numeric `user_profiles` id rides along as a property because that is what
 * every other table joins on. The org goes through group analytics rather than a property,
 * with no properties of its own — an organisation's name is the customer's, not ours to store.
 *
 * Sign-out is NOT handled here: `reset()` belongs next to the code that clears the session
 * (AuthStore.clearAuthState), not in an unmount effect that also runs on a route change.
 */
export function PostHogIdentity() {
  const { userId, userProfile } = useAuth();
  const { currentOrganization } = useOrganizationContext();
  const profileId = userProfile?.id;
  const orgId = currentOrganization?.id;

  useEffect(() => {
    if (!userId) return;
    // No props at all until the profile is loaded — `{ profile_id: undefined }` is a person
    // property being written with nothing in it, and this effect runs before that read lands.
    identifyUser(userId, profileId === undefined ? undefined : { profile_id: profileId });
  }, [userId, profileId]);

  useEffect(() => {
    if (!orgId) return;
    setOrganisation(orgId);
  }, [orgId]);

  return null;
}
